"use client";

import * as React from "react";

type SidebarVariant = "sidebar" | "floating" | "inset";

interface ThemeSettingsContextType {
  sidebarVariant: SidebarVariant;
  setSidebarVariant: (variant: SidebarVariant) => void;
}

const ThemeSettingsContext = React.createContext<ThemeSettingsContextType | null>(null);

export function ThemeSettingsProvider({ children }: { children: React.ReactNode }) {
  const [sidebarVariant, setSidebarVariantState] = React.useState<SidebarVariant>("sidebar");
  const [mounted, setMounted] = React.useState(false);

  // Load from localStorage on mount
  React.useEffect(() => {
    setMounted(true);
    const saved = localStorage.getItem("sidebar-variant") as SidebarVariant;
    // Map "default" to "sidebar" for the Sidebar component prop
    if (saved === "default" as unknown as SidebarVariant) {
      setSidebarVariantState("sidebar");
    } else if (saved === "floating" || saved === "inset") {
      setSidebarVariantState(saved);
    }
  }, []);

  const setSidebarVariant = React.useCallback((variant: SidebarVariant) => {
    setSidebarVariantState(variant);
    // Map for storage: "sidebar" -> "default"
    const storageValue = variant === "sidebar" ? "default" : variant;
    localStorage.setItem("sidebar-variant", storageValue);
  }, []);

  const value = React.useMemo(() => ({
    sidebarVariant: mounted ? sidebarVariant : "sidebar",
    setSidebarVariant,
  }), [sidebarVariant, setSidebarVariant, mounted]);

  return (
    <ThemeSettingsContext.Provider value={value}>
      {children}
    </ThemeSettingsContext.Provider>
  );
}

export function useThemeSettings() {
  const context = React.useContext(ThemeSettingsContext);
  if (!context) {
    throw new Error("useThemeSettings must be used within ThemeSettingsProvider");
  }
  return context;
}

/**
 * Hook to get sidebar variant for use in Sidebar component
 * Maps "default" (user-facing) to "sidebar" (component prop)
 */
export function useSidebarVariant(): SidebarVariant {
  const [variant, setVariant] = React.useState<SidebarVariant>("sidebar");
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);

    const readVariant = (): SidebarVariant => {
      const saved = localStorage.getItem("sidebar-variant");
      if (saved === "floating" || saved === "inset") return saved;
      return "sidebar";
    };

    setVariant(readVariant());

    const handleStorageChange = () => setVariant(readVariant());
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("sidebar-variant-change", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("sidebar-variant-change", handleStorageChange);
    };
  }, []);

  // Return default during SSR and first render to avoid hydration mismatch
  return mounted ? variant : "sidebar";
}
